// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — the microphone HeightSource (principle #23, the primary input).
//
// getUserMedia({audio}) → an AnalyserNode → a block of PCM per frame → the PURE
// derivation (pitch.ts: detectPitch → pitchToHeight) → a smoothed 0..1 height. It
// computes a number and nothing else: no audio is recorded, buffered to disk, or put
// on any wire. Every track is stopped on dispose(), so the OS mic indicator goes out
// the moment a game ends.
//
// getUserMedia must be called from a user gesture (iOS) and needs a secure context
// (https / localhost). Absence or denial is not a wall — main.ts falls back to the
// touch/keyboard source instead.

import { detectPitch, pitchToHeight, rmsOf, HeightSmoother } from './pitch';
import type { HeightSource } from './input';

const AudioCtor = (): typeof AudioContext | null =>
  window.AudioContext ||
  (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ||
  null;

/** True when the browser can even attempt a mic capture. */
export function micSupported(): boolean {
  return !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function' && AudioCtor());
}

export interface MicSource extends HeightSource {
  kind: 'mic';
  /** Resolves true once a real (non-silent) audio frame has been read. */
  ready(timeoutMs: number): Promise<boolean>;
}

/**
 * Ask for the mic (from within a tap) and return a live height source. Rejects if
 * permission is denied or capture fails — the caller uses the touch fallback then.
 */
export async function requestMicSource(): Promise<MicSource> {
  const AC = AudioCtor();
  if (!navigator.mediaDevices?.getUserMedia || !AC) throw new Error('mic-unsupported');

  // No AGC/noise-suppression: they fight the fundamental we are trying to track.
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
  });

  const ctx = new AC();
  if (ctx.state === 'suspended') await ctx.resume();
  const srcNode = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0;
  srcNode.connect(analyser);
  const buf = new Float32Array(analyser.fftSize);
  const sr = ctx.sampleRate;

  const smoother = new HeightSmoother(0.5, 80);
  let disposed = false;
  let gotSound = false;
  const waiters: Array<() => void> = [];

  return {
    kind: 'mic',
    height(dtMs: number): number {
      if (disposed) return smoother.value();
      analyser.getFloatTimeDomainData(buf);
      if (!gotSound && rmsOf(buf) > 0.006) {
        gotSound = true;
        while (waiters.length) waiters.shift()!();
      }
      const hz = detectPitch(buf, sr);
      const target = hz !== null ? pitchToHeight(hz) : null; // silence → hold last height
      return smoother.to(target, dtMs);
    },
    reset(): void {
      smoother.set(0.5);
    },
    ready(timeoutMs: number): Promise<boolean> {
      if (gotSound) return Promise.resolve(true);
      return new Promise<boolean>((resolve) => {
        const to = setTimeout(() => resolve(false), timeoutMs);
        waiters.push(() => {
          clearTimeout(to);
          resolve(true);
        });
      });
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      try {
        srcNode.disconnect();
      } catch {
        /* already gone */
      }
      for (const t of stream.getTracks()) {
        try {
          t.stop();
        } catch {
          /* best effort */
        }
      }
      try {
        void ctx.close();
      } catch {
        /* best effort */
      }
    },
  };
}
